import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import {
  HeaderThElement,
  PagerTargetEvent,
} from "@progress/kendo-react-data-tools";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import IconButton from "../../components/common/IconButton";
import { FiEdit } from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { clearHotelBlockID, setHotelBlockID } from "./hotelBlockSlice";
import {
  ActiveInactiveHotelBlock,
  deleteHotelBlock,
  getAllHotelBlocks,
} from "./services/hotelBlock.services";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Switch } from "@progress/kendo-react-inputs";
import AlertBox from "../../components/common/AlertBox";
import { checkAcessRights } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";

const HotelBlock: React.FC = () => {
  const navigate = useNavigate();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  const handleHotelBlockCreate = () => {
    navigate("/hotelblock/create");
  };

  return (
    <>
      <DeleteHotelBlockDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0px 20px",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Hotel Block List
          </Typography.h4>
          {isAddAccess && (
            <Button
              onClick={handleHotelBlockCreate}
              fillMode={"solid"}
              themeColor={"primary"}
            >
              Add New
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <HotelBlockGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const HotelBlockGridComponent: React.FC = () => {
  const dispatch = useAppDispatch();
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);

  const HotelBlockList = useAppSelector(
    (state) => state.hotelBlock.HotelBlockList,
  );
  const loading = useAppSelector((state) => state.hotelBlock.loading);

  useEffect(() => {
    dispatch(getAllHotelBlocks());
  }, []);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();
  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? HotelBlockList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const MyEditCommandCell = (props: any) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const handleEditHotelBlock = (ID: number) => {
      navigate("/hotelblock/edit", { state: { HotelBlockID: ID } });
    };
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteHotelBlock"));
      dispatch(setHotelBlockID(ID));
    };
    return (
      <td
        {...props.tdProps}
        className={`action-td ${props.className}`}
        style={{ ...props.tdProps.style }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            onClick={() => handleEditHotelBlock(props.dataItem.ID)}
            icon={FiEdit}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "20px" }}
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            className="color-red"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
            icon={MdDelete}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
      </td>
    );
  };
  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${
        props.index === 4 || props.index === 5 ? "locked-header" : ""
      }`}
    >
      {props.children}
    </HeaderThElement>
  );
  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };
  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );
  const MyActiveInActtiveCommandCell = (props: any) => {
    const [IsActive, setIsActive] = useState<boolean>(props.dataItem.IsActive);

    const handleActiveInActive = () => {
      setIsActive(!IsActive);
      const data = {
        ID: props.dataItem.ID,
        IsActive: !IsActive,
      };
      dispatch(ActiveInactiveHotelBlock(data)).then((response: any) => {
        if (response.payload?.data?.Status === 200) {
          dispatch(getAllHotelBlocks());
        } else {
          dispatch(getAllHotelBlocks());
        }
      });
    };

    return (
      <td
        {...props.tdProps}
        className={props.className}
        style={{ ...props.tdProps.style, textAlign: "center" }}
      >
        <Tooltip anchorElement="target" position="top" parentTitle={true}>
          <div title={`Set ${IsActive ? "Inactive" : "Active"}.`}>
            <Switch
              disabled={isEditAccess ? false : true}
              name="IsActive"
              checked={IsActive}
              onChange={handleActiveInActive}
              size="small"
            />
          </div>
        </Tooltip>
      </td>
    );
  };

  const gridRef = useRef<any>(null);

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      {HotelBlockList && HotelBlockList.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
          className="responsive-table"
          filterable={true}
          filter={filter}
          data={
            filter
              ? filterBy(HotelBlockList, filter).slice(
                  page.skip,
                  page.take + page.skip,
                )
              : HotelBlockList.slice(page.skip, page.take + page.skip)
          }
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={HotelBlockList.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column field="TourName" title="Tour" width={300} />
          <Column field="SectorName" title="Sector" width={200} />
          <Column field="TourDate" title="Tour Date" width={200} />
          <Column
            field="TotalNoOfRooms"
            title="Total No Of Rooms"
            width={200}
          />
          <Column
            field="ID"
            title="Status"
            cells={{ data: MyActiveInActtiveCommandCell }}
            width={"90px"}
            filterable={false}
            locked={true}
          />
          {(isEditAccess || isDeleteAccess) && (
            <Column
              field="ID"
              title="Actions"
              cells={{ data: MyEditCommandCell }}
              width={"110px"}
              filterable={false}
              locked={true}
            />
          )}
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteHotelBlockDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const HotelBlockID = useAppSelector((state) => state.hotelBlock.HotelBlockID);
  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteHotelBlock(ID));
      if (response.meta.requestStatus === "fulfilled") {
        dispatch(getAllHotelBlocks());
        dispatch(closeDialog());
        dispatch(clearHotelBlockID());
      } else {
        dispatch(getAllHotelBlocks());
        dispatch(closeDialog());
        dispatch(clearHotelBlockID());
      }
    }
  };
  return (
    <>
      {dialogName === "deleteHotelBlock" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Hotel Block"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Hotel Block?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(HotelBlockID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};
export default HotelBlock;
